import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Check, Trash2, Loader2 } from "lucide-react";
import { useApi, inr, errMsg } from "../lib/api";
import { useMember } from "../lib/member";

const TYPES = { PF: "EPF", ESIC: "ESIC" };

const Confirm = ({ c, onDone }) => {
  const api = useApi();
  const remove = async () => {
    try { await api.delete(`/challans/${c.id}`); toast.success("Upload removed — pick the right file"); onDone(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const mismatch = c.ocr_wage_month && c.ocr_wage_month !== c.billing_month_year;
  return (
    <div className="mt-3 rounded-lg border border-[#1F2A3E] bg-[#0B1017] p-3 text-xs grid gap-2" data-testid={`quick-upload-confirm-${c.challan_type}`}>
      <div className="font-semibold text-sm">{c.challan_type === "EPF" ? "PF" : "ESIC"} challan uploaded — please check what we read</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["TRRN / challan no.", c.trrn_extracted || "not found"], ["Amount", inr(c.total_amount_extracted || 0)], ["Wage month", c.ocr_wage_month || "not found"],
          ["Read confidence", `${Math.round((c.ocr_confidence_score || 0) > 1 ? c.ocr_confidence_score : (c.ocr_confidence_score || 0) * 100)}%`]].map(([l, v]) => (
          <div key={l}><div className="text-slate-500">{l}</div><div className="font-mono text-slate-100">{v}</div></div>))}
      </div>
      {mismatch && <div className="text-amber-300">This challan looks like it is for {c.ocr_wage_month}, not {c.billing_month_year}.</div>}
      {c.duplicate_trrn && <div className="text-rose-300">This TRRN was already uploaded before.</div>}
      <div className="flex gap-2">
        <button onClick={() => onDone(false)} className="inline-flex items-center gap-1 rounded-md bg-[#00E599] px-3 py-1.5 font-semibold text-[#0B1017]" data-testid="quick-upload-ok"><Check size={13} />Looks right</button>
        <button onClick={remove} className="inline-flex items-center gap-1 rounded-md border border-[#1F2A3E] px-3 py-1.5 text-slate-300 hover:text-white" data-testid="quick-upload-remove"><Trash2 size={13} />Remove</button>
      </div>
      <div className="text-slate-500">Your company will verify it. Once verified, any deadline hold is released automatically.</div>
    </div>);
};

export const QuickChallanUpload = ({ month, missing, onUploaded }) => {
  const api = useApi();
  const { me } = useMember();
  const refs = useRef({});
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState([]);
  const pick = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    const fd = new FormData();
    fd.append("vendor_id", me.vendor_id); fd.append("billing_month", month); fd.append("challan_type", TYPES[kind]); fd.append("file", file);
    try { const c = (await api.post("/challans", fd)).data; setDone((d) => [...d.filter((x) => x.challan_type !== c.challan_type), c]); toast.success(`${kind} challan uploaded`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  const close = (c, removed) => { setDone((d) => d.filter((x) => x.id !== c.id)); if (!removed) onUploaded?.(); };
  return (
    <div className="basis-full" data-testid="quick-upload">
      <div className="flex flex-wrap gap-2">
        {missing.map((k) => (
          <label key={k} className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-[#00E599] px-3 py-1.5 text-sm font-semibold text-[#0B1017] hover:brightness-110 ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={`quick-upload-${k}`}>
            {busy === k ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}{busy === k ? "Reading challan…" : `Upload ${k} challan`}
            <input ref={(el) => { refs.current[k] = el; }} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => { pick(k, e.target.files?.[0]); e.target.value = ""; }} data-testid={`quick-upload-input-${k}`} />
          </label>))}
      </div>
      {done.map((c) => <Confirm key={c.id} c={c} onDone={(removed) => close(c, removed)} />)}
    </div>);
};
